import { Suspense, useEffect } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { useAuthSelector } from "../../store/auth";
import Navbar from "../title-bar/Navbar";
import FallbackUI from "../fallback-ui/FallbackUI";
import AppBootstrapper from "../common/AppBootstrapper";
import Admin from "../admin/AdminComponent";
import { Box } from "@mui/material";
import { useLogout } from "../../hooks/useLogout";
import { setLogoutHandler } from "../../services";

export const MainLayout: React.FC = () => {
  const { isAuthenticated, canManageUsers, canManageConfiguration } = useAuth();
  const { user } = useAuthSelector();
  const { logout } = useLogout();
  const location = useLocation();

  const [queryParams, setQueryParams] = useSearchParams();
  const admin = queryParams.get("admin");

  // initialize logout handler so that consumers (such as axios interceptor) can
  // access as soon as authenticated routes are available
  useEffect(() => {
    if (logout) {
      setLogoutHandler(logout);
    }

    return () => {
      setLogoutHandler(null);
    };
  }, []);

  const handleCloseAdminSetttings = () => {
    const params = new URLSearchParams(queryParams);
    params.delete("admin");
    setQueryParams(params);
  };

  // Superusers are platform-level and have no organisation, so the org-facing app
  // can't load a tenant config for them. Keep them inside the cross-tenant Admin
  // Console rather than dropping them into a broken app. The console itself now
  // lives under this layout, so only redirect when they stray off an /admin path
  // (redirecting while already on /admin would loop and never render the console).
  if (isAuthenticated && user?.is_superuser && !location.pathname.startsWith("/admin")) {
    return <Navigate to='/admin' replace />;
  }

  return (
    <div className='main-layout'>
      {/* Show the admin settings page — only for users with admin permission */}
      {admin === "true" && (canManageConfiguration || canManageUsers) && <Admin onClose={handleCloseAdminSetttings} />}

      {/* Bootstrap application state from URL query params */}
      <AppBootstrapper />

      {/* For the time being, hide the Navbar for the index route as we are showing APPContent, which gonna be replace with Home later */}
      {isAuthenticated && <Navbar />}
      <Box className='content-area' sx={{ backgroundColor: "grey.700" }}>
        <Suspense fallback={<FallbackUI />}>
          <Outlet />
        </Suspense>
      </Box>
    </div>
  );
};
